import { type QueryClient, useQuery } from "@tanstack/react-query";

import { useCurrentOrganization } from "@/core/hooks/use-current-organization";
import { cacheDetails } from "@/core/lib/detail-cache";
import type { Datasource } from "@/openapi";
import { getDatasourceByIdQueryKey } from "@/openapi/@tanstack/react-query.gen";
import { getDatasourcesPage } from "@/openapi/sdk.gen";

const DATASOURCE_STALE_TIME_MS = 2 * 60 * 1000;
// the page endpoint's limit cap
const MAX_IDS_PER_REQUEST = 100;

export const datasourcesByIdsQueryKey = (
  organizationId: string,
  ids?: string[],
) =>
  // without ids the key is a prefix for every lookup in the org: an `ids:
  // undefined` entry would only match keys that also have none
  [
    {
      _id: "datasourcesByIds",
      path: { org_id: organizationId },
      ...(ids ? { ids } : {}),
    },
  ] as const;

const detailKey = (organizationId: string, id: string) =>
  getDatasourceByIdQueryKey({
    path: { org_id: organizationId, datasource_id: id },
  });

/** writes each datasource under its own key, dated by the response; see cacheDetails */
export function cacheDatasourceDetails(
  queryClient: QueryClient,
  organizationId: string,
  datasources: Datasource[],
  updatedAt = Date.now(),
) {
  cacheDetails(
    queryClient,
    (id) => detailKey(organizationId, id),
    datasources,
    updatedAt,
  );
}

/**
 * The datasources with these ids that the caller can read, in the order asked.
 * Fresh cached entries are reused, so only ids not seen recently hit the server,
 * batched rather than fetched one by one.
 */
export async function fetchDatasourcesByIds(
  queryClient: QueryClient,
  organizationId: string,
  ids: string[],
): Promise<Datasource[]> {
  const uniqueIds = [...new Set(ids)];
  const missingIds = uniqueIds.filter(
    (id) =>
      queryClient
        .getQueryCache()
        .find({ queryKey: detailKey(organizationId, id), exact: true })
        ?.isStaleByTime(DATASOURCE_STALE_TIME_MS) ?? true,
  );

  const chunks: string[][] = [];
  for (let i = 0; i < missingIds.length; i += MAX_IDS_PER_REQUEST) {
    chunks.push(missingIds.slice(i, i + MAX_IDS_PER_REQUEST));
  }
  const pages = await Promise.all(
    chunks.map((chunk) =>
      getDatasourcesPage({
        path: { org_id: organizationId },
        query: { ids: chunk, limit: chunk.length },
        throwOnError: true,
      }),
    ),
  );
  const fetched = new Map(
    pages.flatMap(({ data }) => data.items).map((d) => [d.id, d]),
  );
  cacheDatasourceDetails(queryClient, organizationId, [...fetched.values()]);

  // a refetched id the server left out is gone or no longer readable, so its
  // stale cached copy must not stand in for it
  const missing = new Set(missingIds);
  return uniqueIds
    .map((id) =>
      missing.has(id)
        ? fetched.get(id)
        : queryClient.getQueryData<Datasource>(detailKey(organizationId, id)),
    )
    .filter((datasource) => datasource !== undefined);
}

/** `organizationId` defaults to the user's own, for screens scoped to another org */
export function useDatasourcesByIds(ids: string[], organizationId?: string) {
  const { organizationId: currentOrganizationId } = useCurrentOrganization({
    throw: true,
  });
  const orgId = organizationId ?? currentOrganizationId;
  const sortedIds = [...new Set(ids)].sort((a, b) => a.localeCompare(b));

  return useQuery({
    queryKey: datasourcesByIdsQueryKey(orgId, sortedIds),
    queryFn: ({ client }) => fetchDatasourcesByIds(client, orgId, sortedIds),
    enabled: sortedIds.length > 0,
    staleTime: DATASOURCE_STALE_TIME_MS,
  });
}
